/**
 * Claims an ITEM owns in the MIDI matrix (2026-10-18, OPBridge S317's ask,
 * shape agreed in OPApps/opbridge-opbuttons.md 2026-10-13).
 *
 * A CC picked for a control from its Inspector claims that square under the
 * control's name, on a device called "OPButtons", so the matrix shows which
 * button uses which CC. The claim carries the item's id (`MatrixClaim.itemId`),
 * which is what lets it follow the item: a new binding moves it, a new name
 * relabels it, an unbound or deleted item removes it. A claim made by hand in
 * the matrix viewer has no `itemId` and is never touched from here.
 *
 * Claims are rig-wide and items are per project, so nothing here ever removes
 * a claim because its item is MISSING from what is on screen: switching
 * project would wipe every other project's claims. Removal follows an actual
 * edit or delete of that item (`layout/store.ts`).
 *
 * Pure: library in, library out (the store wraps these). Returns the SAME
 * library object when nothing changes, so a no-op edit writes no doc.
 */

import type { MatrixClaim, MatrixLibrary } from '@opbuttons/schema';
import type { LayoutItem } from '../layout/projectStore.ts';
import { addClaimAt, removeClaim, type SlotLocator } from './slotsEdit.ts';
import { noteLabelFor } from './noteNames.ts';

/** The matrix device every item-owned claim names. */
export const OPBUTTONS_DEVICE_NAME = 'OPButtons';

/** Where an item's binding sits in the matrix, or null when it has none
 *  (a kind the Inspector cannot pick for, or the fresh CC 0 / ch 0 default,
 *  which the Inspector itself reads as unbound). Same space rule as the
 *  Inspector's `pickSpace`. */
export function itemSlotLocator(item: LayoutItem): SlotLocator | null {
  const it = item as unknown as { kind: string; channel?: number; ccNumber?: number; actionType?: string; note?: number; program?: number };
  if (it.kind !== 'button' && it.kind !== 'lfo' && it.kind !== 'fader' && it.kind !== 'radial') return null;
  if (typeof it.channel !== 'number') return null;
  // An item's channel is 0-based, a matrix slot's 1..16: the picker hands
  // `pick(ch - 1, ...)` to the Inspector (MatrixPicker.tsx), so this undoes it.
  const channel = it.channel + 1;
  const at = it.kind === 'button' ? (it.actionType ?? 'cc') : 'cc';
  if (at === 'noteon') return typeof it.note === 'number' ? { kind: 'note', channel, noteLabel: noteLabelFor(it.note) } : null;
  if (at === 'pc') return typeof it.program === 'number' ? { kind: 'pc', channel, pcNumber: it.program } : null;
  if (typeof it.ccNumber !== 'number') return null;
  if (it.ccNumber === 0 && it.channel === 0) return null;
  return { kind: 'cc', channel, ccNumber: it.ccNumber };
}

/** The label an item's claim carries: its name, else its kind (the picker's
 *  own heading rule). */
export function itemClaimLabel(item: LayoutItem): string {
  return (item as { name?: string }).name?.trim() || item.kind.toUpperCase();
}

function ownedClaims(library: MatrixLibrary, itemId: string): Array<{ claim: MatrixClaim; loc: SlotLocator }> {
  const out: Array<{ claim: MatrixClaim; loc: SlotLocator }> = [];
  for (const s of library.slots) {
    for (const c of s.claims) {
      if (c.itemId !== itemId) continue;
      const loc: SlotLocator = s.kind === 'note'
        ? { kind: 'note', channel: s.channel, noteLabel: s.noteLabel ?? '' }
        : s.kind === 'pc'
          ? { kind: 'pc', channel: s.channel, pcNumber: s.pcNumber ?? 0 }
          : { kind: 'cc', channel: s.channel, ccNumber: s.ccNumber ?? 0 };
      out.push({ claim: c, loc });
    }
  }
  return out;
}

const sameLoc = (a: SlotLocator, b: SlotLocator): boolean => {
  if (a.kind !== b.kind || a.channel !== b.channel) return false;
  if (a.kind === 'cc') return a.ccNumber === (b as typeof a).ccNumber;
  if (a.kind === 'pc') return a.pcNumber === (b as typeof a).pcNumber;
  return a.noteLabel.toUpperCase() === (b as typeof a).noteLabel.toUpperCase();
};

export interface ClaimIds {
  /** Fresh ulids, used only if the thing they name has to be created. */
  deviceId: string;
  slotId: string;
  claimId: string;
}

/** Make `item` own exactly one claim, at its current binding: created if it
 *  has none, moved if the binding changed, relabelled if the name changed.
 *  An item with no binding owns none. Creates the OPButtons device on first
 *  use. */
export function placeItemClaim(library: MatrixLibrary, item: LayoutItem, ids: ClaimIds): MatrixLibrary {
  const loc = itemSlotLocator(item);
  const label = itemClaimLabel(item);
  const owned = ownedClaims(library, item.id);
  if (loc && owned.length === 1 && sameLoc(owned[0]!.loc, loc) && owned[0]!.claim.controlLabel === label) return library;
  let next = library;
  for (const o of owned) next = removeClaim(next, o.claim.id);
  if (!loc) return next;
  let device = next.devices.find((d) => d.name === OPBUTTONS_DEVICE_NAME);
  if (!device) {
    device = { id: ids.deviceId, name: OPBUTTONS_DEVICE_NAME, color: 'cyan' };
    next = { ...next, devices: [...next.devices, device] };
  }
  const { library: placed } = addClaimAt(next, loc, { slotId: ids.slotId, claimId: ids.claimId, deviceId: device.id, controlLabel: label });
  // `addClaimAt` knows nothing of owners; stamp this claim as the item's.
  return {
    ...placed,
    slots: placed.slots.map((s) => (s.claims.some((c) => c.id === ids.claimId)
      ? { ...s, claims: s.claims.map((c) => (c.id === ids.claimId ? { ...c, itemId: item.id } : c)) }
      : s)),
  };
}

/** Keep an item's claim in step after an edit - but only if it already owns
 *  one. An item never picked from the matrix gains nothing from an edit. */
export function followItemClaim(library: MatrixLibrary, item: LayoutItem, ids: ClaimIds): MatrixLibrary {
  return ownedClaims(library, item.id).length ? placeItemClaim(library, item, ids) : library;
}

/** Remove every claim these items own (they were deleted). */
export function dropItemClaims(library: MatrixLibrary, itemIds: readonly string[]): MatrixLibrary {
  const doomed = new Set(itemIds);
  let next = library;
  for (const s of library.slots) {
    for (const c of s.claims) if (c.itemId && doomed.has(c.itemId)) next = removeClaim(next, c.id);
  }
  return next;
}
